import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import type { DisposalReason } from '@/types/settings';

export default function DisposalReasonsPage(): React.ReactElement {
  const [reasons, setReasons] = useState<DisposalReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<DisposalReason | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadReasons() {
      try {
        setLoading(true);
        setError(null);
        const data = await dataService.getDisposalReasons();
        setReasons(data);
      } catch (err) {
        console.error('Failed to load disposal reasons:', err);
        setError('Failed to load disposal reasons. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadReasons();
  }, []);

  const [formData, setFormData] = useState<Partial<DisposalReason>>({
    code: '',
    description: '',
  });

  const handleOpenDialog = (reason?: DisposalReason) => {
    if (reason) {
      setEditingReason(reason);
      setFormData(reason);
    } else {
      setEditingReason(null);
      setFormData({
        code: '',
        description: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingReason) {
        if (isDemoMode()) {
          setReasons(
            reasons.map((reason) =>
              reason.id === editingReason.id
                ? { ...reason, ...formData, modifiedDate: new Date().toISOString() }
                : reason
            )
          );
        } else {
          const updated = await dataService.updateDisposalReason(editingReason.id, {
            ...formData,
            modifiedDate: new Date().toISOString(),
          } as DisposalReason);
          setReasons(reasons.map((reason) => (reason.id === updated.id ? updated : reason)));
        }
      } else {
        const newReason: DisposalReason = {
          id: String(reasons.length + 1),
          code: formData.code || '',
          description: formData.description || '',
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        
        if (isDemoMode()) {
          setReasons([...reasons, newReason]);
        } else {
          const created = await dataService.createDisposalReason(newReason);
          setReasons([...reasons, created]);
        }
      }
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Failed to save disposal reason:', err);
      alert('Failed to save disposal reason. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this disposal reason?')) {
      try {
        if (isDemoMode()) {
          setReasons(reasons.filter((reason) => reason.id !== id));
        } else {
          await dataService.deleteDisposalReason(id);
          setReasons(reasons.filter((reason) => reason.id !== id));
        }
      } catch (err) {
        console.error('Failed to delete disposal reason:', err);
        alert('Failed to delete disposal reason. Please try again.');
      }
    }
  };

  const filteredReasons = reasons.filter((reason) =>
    reason.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    reason.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Disposal Reasons</h2>
              <p className="text-muted-foreground">
                Manage reasons for asset disposal
              </p>
            </div>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Reason
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search disposal reasons..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  Loading disposal reasons...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            ) : filteredReasons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  No disposal reasons found
                </TableCell>
              </TableRow>
            ) : (
              filteredReasons.map((reason) => (
                <TableRow key={reason.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                      {reason.code}
                    </div>
                  </TableCell>
                  <TableCell>{reason.description}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(reason)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(reason.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingReason ? 'Edit Disposal Reason' : 'Add Disposal Reason'}
            </DialogTitle>
            <DialogDescription>
              {editingReason
                ? 'Update the disposal reason details below.'
                : 'Enter the details for the new disposal reason.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
                placeholder="e.g., SOLD"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="e.g., Asset sold to third party"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.code || !formData.description}
            >
              {editingReason ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
